import React, { useState, useEffect } from 'react';
import { Users, Sparkles, RefreshCw, Briefcase, Globe, Lightbulb, Wand2 } from 'lucide-react';
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import VolunteerBadge from "./VolunteerBadge";

const GuestMatches = ({ token }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [computeState, setComputeState] = useState("ready"); // ready | computing | stale_refreshing

  const fetchMatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data.matches || []);
        setComputeState(data.state || "ready");
        // If still computing in the background, poll again shortly
        if (data.state === "computing") {
          setTimeout(() => { fetchMatches(); }, 8000);
        }
      } else {
        // Surface the actual status so we can debug production issues
        let body = "";
        try { body = await response.text(); } catch (_) { /* ignore */ }
        const detail = body && body.length < 200 ? ` — ${body}` : "";
        setError(`Server returned ${response.status}${detail}`);
        console.error('AI matches HTTP error', response.status, body);
      }
    } catch (err) {
      setError(`Network error: ${err?.message || 'connection failed'}`);
      console.error('Error fetching matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const pollRegenStatus = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/regenerate-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.in_progress) {
        toast.message(`Regenerating matches… ${data.completed}/${data.total}`);
        setTimeout(pollRegenStatus, 5000);
      } else {
        const failedCount = (data.failed || []).length;
        toast.success(
          failedCount > 0
            ? `Done. ${data.completed - failedCount}/${data.total} succeeded, ${failedCount} failed`
            : `Regenerated matches for ${data.total} guests ✨`
        );
        setRegenerating(false);
        fetchMatches();
      }
    } catch (e) {
      console.error('regen poll failed', e);
      setRegenerating(false);
    }
  };

  const regenerateForEveryone = async () => {
    if (!window.confirm("Recompute AI matches for every guest? This runs in the background and takes 1–3 minutes.")) return;
    setRegenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/regenerate-matches`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let data = null;
      try { data = await response.json(); } catch (_) { /* not JSON */ }
      console.log('regenerate response', response.status, data);
      if (response.ok && data?.success) {
        toast.success(`Started — recomputing for ${data.queued} guests in the background…`);
        setTimeout(pollRegenStatus, 5000);
      } else {
        toast.error(`Failed (${response.status}): ${data?.detail || 'unknown error'}`);
        setRegenerating(false);
      }
    } catch (err) {
      toast.error(`Network error: ${err?.message || 'connection failed'}`);
      console.error('regenerate fetch failed', err);
      setRegenerating(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, [token]);

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-stone-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="font-serif text-lg sm:text-xl text-[#1A1A1A] truncate">AI Guest Matches</h2>
            <p className="text-xs sm:text-sm text-[#1A1A1A]/60 truncate">People you should connect with</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-stretch sm:self-auto">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={regenerateForEveryone}
              disabled={regenerating}
              className="flex-1 sm:flex-none rounded-full border-[#C85A38]/40 text-[#C85A38] hover:bg-[#C85A38]/5 text-xs sm:text-sm px-3"
              data-testid="regen-matches-btn"
              title="Recompute AI matches for every guest"
            >
              <Wand2 className={`w-4 h-4 sm:mr-2 ${regenerating ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{regenerating ? 'Regenerating…' : 'Regenerate all'}</span>
              <span className="sm:hidden ml-1">{regenerating ? '…' : 'Regen'}</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMatches}
            disabled={loading}
            className="flex-1 sm:flex-none rounded-full text-xs sm:text-sm px-3"
            data-testid="refresh-matches-btn"
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden ml-1">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading && matches.length === 0 ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-[#F8F6F3] rounded-xl">
              <div className="w-12 h-12 bg-stone-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-stone-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-stone-200 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-[#1A1A1A]/60 mb-4">{error}</p>
          <Button onClick={fetchMatches} variant="outline" className="rounded-full">
            Try Again
          </Button>
        </div>
      ) : matches.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-[#1A1A1A]/20 mx-auto mb-4" />
          {computeState === "computing" ? (
            <>
              <p className="text-[#1A1A1A]/70 font-medium">Crunching matches for you…</p>
              <p className="text-sm text-[#1A1A1A]/40 mt-1">This takes ~20 seconds the first time. We&apos;ll refresh automatically.</p>
            </>
          ) : (
            <>
              <p className="text-[#1A1A1A]/60">No matches found yet.</p>
              <p className="text-sm text-[#1A1A1A]/40 mt-1">
                Make sure guests have an occupation, project, nationality or fun fact filled in.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {matches.map((match, index) => (
            <div 
              key={match.id || index}
              className="p-4 bg-[#F8F6F3] rounded-xl hover:bg-[#F0EDE3] transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative">
                  {match.avatar ? (
                    <img 
                      src={match.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${match.avatar}` : match.avatar}
                      alt={match.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C85A38] to-[#D06A4F] flex items-center justify-center text-white font-medium">
                      {match.name?.charAt(0) || '?'}
                    </div>
                  )}
                  {/* Match Score Badge */}
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold border-2 border-white">
                    {match.match_score}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-medium text-[#1A1A1A]">{match.name}</h3>
                    <VolunteerBadge show={match.is_volunteer} />
                    {match.nationality && (
                      <span className="text-xs text-[#1A1A1A]/50 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {match.nationality}
                      </span>
                    )}
                  </div>
                  
                  {match.occupation && (
                    <div className="flex items-center gap-1 text-sm text-[#1A1A1A]/70 mb-1">
                      <Briefcase className="w-3 h-3" />
                      {match.occupation}
                    </div>
                  )}
                  
                  {match.current_project && (
                    <div className="flex items-center gap-1 text-sm text-[#1A1A1A]/70 mb-2">
                      <Lightbulb className="w-3 h-3" />
                      {match.current_project}
                    </div>
                  )}

                  {/* Match Reason */}
                  {match.match_reason && (
                    <div className="flex items-start gap-2 mt-2 p-2 bg-white/50 rounded-lg">
                      <Sparkles className="w-4 h-4 text-[#C85A38] flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-[#1A1A1A]/70">{match.match_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-stone-100 text-center">
        <p className="text-xs text-[#1A1A1A]/40">
          <Sparkles className="w-3 h-3 inline mr-1" />
          Powered by AI • Matches based on skills, interests & projects
        </p>
      </div>
    </div>
  );
};

export default GuestMatches;
